import React from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
} from '@mui/material';
import { PersonSearchResult } from '../../types';
import { SortOption } from '../../pages/Search';
import { useNavigate } from 'react-router-dom';

/**
 * SearchResultsList Component
 *
 * Displays search results in a grid layout with sorting options
 *
 * Features:
 * - Grid layout with result cards
 * - Sort dropdown (relevance, name A-Z, name Z-A, birth date)
 * - Result count display
 * - Click result to navigate to profile
 *
 * @component
 * @param {SearchResultsListProps} props - Component props
 * @returns {JSX.Element} Search results list
 */
interface SearchResultsListProps {
  results: PersonSearchResult[];
  sortOption: SortOption;
  onSortChange: (sort: SortOption) => void;
}

export const SearchResultsList: React.FC<SearchResultsListProps> = ({
  results,
  sortOption,
  onSortChange,
}) => {
  const navigate = useNavigate();

  // ===== Event Handlers =====
  const handleSortChange = (event: SelectChangeEvent<SortOption>) => {
    onSortChange(event.target.value as SortOption);
  };

  const handlePersonClick = (personId: string) => {
    navigate(`/people/${personId}`);
  };

  const getInitials = (person: PersonSearchResult): string => {
    const first = person.firstName?.[0] || '';
    const last = person.lastName?.[0] || '';
    return `${first}${last}`.toUpperCase();
  };

  const getLifeYears = (person: PersonSearchResult): string => {
    const birthYear = person.birthDate?.substring(0, 4);
    const deathYear = person.deathDate?.substring(0, 4);

    if (birthYear && deathYear) {
      return `${birthYear}-${deathYear}`;
    } else if (birthYear) {
      return `b. ${birthYear}`;
    } else if (deathYear) {
      return `d. ${deathYear}`;
    }
    return '';
  };

  // ===== Render =====
  return (
    <Box>
      {/* Sort Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {results.length} {results.length === 1 ? 'person' : 'people'} found
        </Typography>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="sort-label">Sort by</InputLabel>
          <Select
            labelId="sort-label"
            id="sort-select"
            value={sortOption}
            label="Sort by"
            onChange={handleSortChange}
          >
            <MenuItem value="relevance">Relevance</MenuItem>
            <MenuItem value="nameAsc">Name (A-Z)</MenuItem>
            <MenuItem value="nameDesc">Name (Z-A)</MenuItem>
            <MenuItem value="birthDateAsc">Birth Date (Oldest)</MenuItem>
            <MenuItem value="birthDateDesc">Birth Date (Newest)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Results Grid */}
      <Grid container spacing={3}>
        {results.map((person) => (
          <Grid item xs={12} sm={6} md={4} key={person.id}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardActionArea onClick={() => handlePersonClick(person.id)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      src={person.primaryPhotoUrl}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'primary.main',
                      }}
                    >
                      {getInitials(person)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {person.firstName} {person.lastName}
                      </Typography>
                      {person.maidenName && (
                        <Typography variant="body2" color="text.secondary">
                          née {person.maidenName}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {getLifeYears(person) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {getLifeYears(person)}
                    </Typography>
                  )}

                  {person.birthPlace && (
                    <Typography variant="body2" color="text.secondary">
                      📍 {person.birthPlace}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Results Summary (Bottom) */}
      {results.length > 10 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing all {results.length} results
          </Typography>
        </Box>
      )}
    </Box>
  );
};
